import React, { useMemo } from 'react';
import { css } from '@emotion/css';
import { Theme, useTheme } from '@mui/material';
import { renderMarkdown } from './renderMarkdown';

interface ContentfulMarkdownProps {
  /** Raw markdown — parsed and sanitized internally. Ignored when `html` is provided. */
  content?: string;
  /** Pre-rendered, already-sanitized HTML (from `renderMarkdown`). Use when the caller also
   *  needs the heading outline and wants to avoid parsing the markdown twice. */
  html?: string;
  variant?: 'default' | 'modal' | 'article';
}

/**
 * Component to render Contentful / Learning Center markdown content.
 *
 * SECURITY: markdown may be authored by non-engineers (CST Learning Center modules); it is
 * sanitized in `renderMarkdown` before being injected. Callers passing `html` must supply the
 * output of `renderMarkdown` — never hand-built or unsanitized HTML.
 */
const ContentfulMarkdown: React.FC<ContentfulMarkdownProps> = ({
  content,
  html,
  variant = 'default'
}) => {
  const theme = useTheme();
  const cls =
    variant === 'modal' ? modalStyles(theme)
    : variant === 'article' ? articleStyles(theme)
    : defaultStyles(theme);

  const htmlContent = useMemo(
    () => (html !== undefined ? html : renderMarkdown(content).html),
    [html, content],
  );

  return (
    <div
      className={cls.markdownContent}
      dangerouslySetInnerHTML={{ __html: htmlContent }}
    />
  );
};

// Styles for UserAgreementPage (default variant)
const defaultStyles = (theme: Theme) => ({
  markdownContent: css`
    color: ${theme?.palette?.text?.primary};
    line-height: 1.6;

    h1 {
      font-size: 2rem;
      font-weight: 600;
      margin-bottom: 8px;
      color: ${theme?.palette?.text?.primary};
    }

    h2 {
      font-size: 1.5rem;
      font-weight: 600;
      margin-top: 32px;
      margin-bottom: 16px;
      color: ${theme?.palette?.text?.primary};
    }

    h3 {
      font-size: 1.25rem;
      font-weight: 600;
      margin-top: 24px;
      margin-bottom: 12px;
      color: ${theme?.palette?.text?.primary};
    }

    p {
      margin-bottom: 16px;
      color: ${theme?.palette?.text?.secondary};
    }

    ul,
    ol {
      margin-bottom: 16px;
      padding-left: 24px;
      color: ${theme?.palette?.text?.secondary};
    }

    li {
      margin-bottom: 8px;
    }

    strong {
      font-weight: 600;
      color: ${theme?.palette?.text?.primary};
    }

    hr {
      margin: 24px 0;
      border: none;
      border-top: 1px solid ${theme?.palette?.divider};
    }

    code {
      background-color: ${theme?.palette?.action?.hover};
      padding: 2px 6px;
      border-radius: 4px;
      font-family: 'Courier New', monospace;
      font-size: 0.9em;
    }

    pre {
      background-color: ${theme?.palette?.action?.hover};
      padding: 16px;
      border-radius: 8px;
      overflow-x: auto;
      margin-bottom: 16px;

      code {
        background-color: transparent;
        padding: 0;
      }
    }

    a {
      color: ${theme?.palette?.primary?.main};
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }

    blockquote {
      border-left: 4px solid ${theme?.palette?.primary?.main};
      padding-left: 16px;
      margin: 16px 0;
      color: ${theme?.palette?.text?.secondary};
      font-style: italic;
    }
  `,
});

// Styles for the Learning Center reader (article variant).
// A comfortable reading treatment: full-ink body copy (not the muted secondary text used by the
// document variants), tighter heading rhythm, soft-panel blockquotes, and bordered media/tables.
// Headings carry a scroll-margin so table-of-contents jumps don't tuck them under a sticky header.
const articleStyles = (theme: Theme) => ({
  markdownContent: css`
    color: ${theme?.palette?.text?.primary};
    font-size: 16px;
    line-height: 1.68;

    h1 {
      font-size: 26px;
      font-weight: 700;
      line-height: 1.25;
      margin: 0 0 12px;
      color: ${theme?.palette?.text?.primary};
      scroll-margin-top: 88px;
    }

    h2 {
      font-size: 22px;
      font-weight: 700;
      line-height: 1.3;
      margin: 40px 0 6px;
      color: ${theme?.palette?.text?.primary};
      scroll-margin-top: 88px;
    }

    h2:first-of-type {
      margin-top: 8px;
    }

    h3 {
      font-size: 18px;
      font-weight: 600;
      line-height: 1.35;
      margin: 28px 0 8px;
      color: ${theme?.palette?.text?.primary};
      scroll-margin-top: 88px;
    }

    p {
      margin: 0 0 18px;
    }

    ul,
    ol {
      margin: 0 0 18px;
      padding-left: 24px;
    }

    li {
      margin-bottom: 9px;
    }

    li::marker {
      color: ${theme?.palette?.text?.disabled};
    }

    strong {
      font-weight: 600;
      color: ${theme?.palette?.text?.primary};
    }

    a {
      color: ${theme?.palette?.secondary?.main};
      text-decoration: none;
      font-weight: 500;

      &:hover {
        text-decoration: underline;
      }
    }

    img {
      display: block;
      max-width: 100%;
      margin: 24px auto;
      border-radius: 8px;
      border: 1px solid ${theme?.palette?.divider};
    }

    hr {
      border: none;
      border-top: 1px solid ${theme?.palette?.divider};
      margin: 32px 0;
    }

    blockquote {
      margin: 22px 0;
      padding: 14px 18px;
      background-color: ${theme?.palette?.action?.hover};
      border: 1px solid ${theme?.palette?.divider};
      border-radius: 10px;

      > :first-of-type {
        margin-top: 0;
      }
      > :last-child {
        margin-bottom: 0;
      }
    }

    code {
      background-color: ${theme?.palette?.action?.hover};
      border: 1px solid ${theme?.palette?.divider};
      padding: 1px 6px;
      border-radius: 5px;
      font-family: 'JetBrains Mono', 'Courier New', monospace;
      font-size: 0.85em;
    }

    pre {
      background-color: ${theme?.palette?.action?.hover};
      border: 1px solid ${theme?.palette?.divider};
      padding: 16px;
      border-radius: 10px;
      overflow-x: auto;
      margin: 0 0 18px;

      code {
        background-color: transparent;
        border: none;
        padding: 0;
        font-size: 13px;
      }
    }

    table {
      display: block;
      width: 100%;
      overflow-x: auto;
      border-collapse: collapse;
      font-size: 14.5px;
      margin: 22px 0;
    }

    th,
    td {
      text-align: left;
      padding: 11px 13px;
      border-bottom: 1px solid ${theme?.palette?.divider};
    }

    thead th {
      font-weight: 600;
      color: ${theme?.palette?.text?.primary};
    }

    td {
      color: ${theme?.palette?.text?.secondary};
    }
  `,
});

// Styles for UserAgreementModal
const modalStyles = (theme: Theme) => ({
  markdownContent: css`
    color: ${theme?.palette?.text?.primary};
    line-height: 1.7;

    h1 {
      font-size: 2.5rem;
      font-weight: 700;
      margin-bottom: 12px;
      color: ${theme?.palette?.text?.primary};
      text-align: center;
    }

    h2 {
      font-size: 1.5rem;
      font-weight: 600;
      margin-top: 40px;
      margin-bottom: 16px;
      color: ${theme?.palette?.text?.primary};
      padding-top: 16px;
    }

    h3 {
      font-size: 1.25rem;
      font-weight: 600;
      margin-top: 28px;
      margin-bottom: 12px;
      color: ${theme?.palette?.text?.primary};
    }

    p {
      margin-bottom: 16px;
      color: ${theme?.palette?.text?.secondary};
      font-size: 1rem;
    }

    ul, ol {
      margin-bottom: 16px;
      padding-left: 28px;
      color: ${theme?.palette?.text?.secondary};
    }

    li {
      margin-bottom: 10px;
      line-height: 1.6;
    }

    strong {
      font-weight: 600;
      color: ${theme?.palette?.text?.primary};
    }

    hr {
      margin: 32px 0;
      border: none;
      border-top: 2px solid ${theme?.palette?.divider};
    }

    code {
      background-color: ${theme?.palette?.action?.hover};
      padding: 2px 8px;
      border-radius: 4px;
      font-family: 'Courier New', monospace;
      font-size: 0.9em;
    }

    pre {
      background-color: ${theme?.palette?.action?.hover};
      padding: 16px;
      border-radius: 8px;
      overflow-x: auto;
      margin-bottom: 16px;

      code {
        background-color: transparent;
        padding: 0;
      }
    }

    a {
      color: ${theme?.palette?.primary?.main};
      text-decoration: none;

      &:hover {
        text-decoration: underline;
      }
    }

    blockquote {
      border-left: 4px solid ${theme?.palette?.primary?.main};
      padding-left: 16px;
      margin: 16px 0;
      color: ${theme?.palette?.text?.secondary};
      font-style: italic;
    }
  `,
});

export default ContentfulMarkdown;
